import { useMemo } from 'react'
import { useQuickHelp, QuickHelpOption } from '@/contexts/quick-help'

/**
 * Quick help options for Employee List page
 * Provides guided tours for common actions
 */
export const useEmployeeListQuickHelp = () => {
    const options: QuickHelpOption[] = useMemo(
        () => [
            {
                id: 'employee-add-new',
                label: 'Thêm nhân viên mới',
                description: 'Tạo tài khoản cho nhân viên mới',
                selector: '[data-tour="create-employee-btn"]',
                tourTitle: 'Thêm nhân viên mới',
                tourDescription:
                    'Nhấn nút này để tạo tài khoản cho nhân viên mới.\n\n• Nhập thông tin cơ bản (tên, email)\n• Chọn phòng ban và chức vụ\n• Hệ thống tự động gửi email xác nhận\n• Nhân viên điền thông tin chi tiết sau khi xác nhận',
            },
            {
                id: 'employee-search',
                label: 'Tìm kiếm nhân viên',
                description: 'Tìm nhanh theo tên, email, mã NV',
                selector: '[data-tour="employee-search"]',
                tourTitle: 'Tìm kiếm nhân viên',
                tourDescription:
                    'Sử dụng ô tìm kiếm để tìm nhân viên nhanh chóng.\n\nTìm kiếm theo:\n• Tên nhân viên\n• Mã nhân viên\n• Email\n• Số điện thoại\n\nKết quả được cập nhật ngay khi bạn gõ.',
            },
            {
                id: 'employee-filter',
                label: 'Lọc danh sách',
                description: 'Hướng dẫn sử dụng bộ lọc',
                selector: '[data-tour="employee-filter-bar"]',
                tourTitle: 'Bộ lọc nhân viên',
                tourDescription:
                    'Sử dụng các bộ lọc để tìm nhân viên theo nhiều tiêu chí.',
                tourSteps: [
                    {
                        selector: '[data-tour="employee-filter-bar"]',
                        title: 'Bộ lọc nâng cao',
                        content:
                            'Lọc nhân viên theo nhiều tiêu chí:\n\n• Loại nhân viên (chính thức/thử việc)\n• Giới tính\n• Trạng thái làm việc\n• Ngày sinh\n• Phòng ban và vị trí',
                    },
                ],
            },
            {
                id: 'employee-export',
                label: 'Xuất danh sách Excel',
                description: 'Export toàn bộ hoặc theo bộ lọc',
                selector: '[data-tour="employee-settings"]',
                tourTitle: 'Export danh sách',
                tourDescription:
                    'Xuất danh sách nhân viên ra file Excel.\n\n• Click vào icon Settings (Settings)\n• Chọn "Export Excel"\n• File xuất theo bộ lọc hiện tại\n• Chứa đầy đủ thông tin nhân viên',
            },
            {
                id: 'employee-edit',
                label: 'Chỉnh sửa thông tin',
                description: 'Cập nhật thông tin nhân viên',
                selector: '[data-tour="employee-data-table"]',
                tourTitle: 'Chỉnh sửa thông tin nhân viên',
                tourDescription:
                    'Để chỉnh sửa thông tin nhân viên:\n\n1. Click vào icon ba chấm (ba chấm) ở cuối dòng\n2. Chọn "Chỉnh sửa thông tin"\n3. Cập nhật các trường cần thiết\n4. Nhấn "Lưu" để hoàn tất',
            },
            {
                id: 'employee-resend-confirmation',
                label: 'Gửi lại link xác nhận',
                description: 'Khi nhân viên chưa xác nhận email',
                tourTitle: 'Gửi lại email xác nhận',
                tourDescription:
                    'Sử dụng khi nhân viên mới chưa xác nhận tài khoản:\n\n1. Tìm nhân viên chưa xác nhận (icon cảnh báo)\n2. Click vào icon ba chấm (ba chấm)\n3. Chọn "Gửi lại xác nhận"\n4. Email mới sẽ được gửi đến nhân viên\n\nLưu ý: Chỉ áp dụng cho nhân viên chưa xác nhận email.',
            },
            {
                id: 'employee-disable-account',
                label: 'Khoá tài khoản',
                description: 'Khi nhân viên nghỉ việc',
                tourTitle: 'Khoá tài khoản nhân viên',
                tourDescription:
                    'Thực hiện khi nhân viên nghỉ việc:\n\n1. Click vào icon ba chấm (ba chấm)\n2. Chọn "Khoá tài khoản" (màu đỏ)\n3. Chọn ngày nghỉ việc\n4. Nhập lý do (tuỳ chọn)\n5. Xác nhận\n\nSau khi khoá:\n• Nhân viên không thể đăng nhập\n• Dữ liệu vẫn được lưu trữ\n• Có thể mở khoá nếu cần',
            },
            {
                id: 'employee-table-settings',
                label: 'Tuỳ chỉnh bảng hiển thị',
                description: 'Cài đặt cỡ chữ, cột hiển thị',
                selector: '[data-tour="employee-settings"]',
                tourTitle: 'Cài đặt bảng',
                tourDescription:
                    'Click vào icon Settings (Settings) để:\n\n• Điều chỉnh cỡ chữ (Nhỏ/Vừa/Lớn)\n• Thay đổi chiều cao hàng\n• Ẩn/hiện các cột\n• Reset về mặc định\n\nCài đặt được lưu tự động.',
            },
            {
                id: 'employee-tabs',
                label: 'Chuyển đổi danh sách',
                description: 'Xem nhân viên hoặc phòng ban',
                selector: '[data-tour="employee-tabs"]',
                tourTitle: 'Tabs danh sách',
                tourDescription:
                    'Chuyển đổi giữa các tab:\n\n• Danh sách nhân viên: Xem tất cả nhân viên\n• Phòng ban: Quản lý cơ cấu tổ chức\n\nMỗi tab có bộ lọc và tính năng riêng.',
            },
            {
                id: 'employee-overview-tour',
                label: 'Xem tour tổng quan',
                description: 'Hướng dẫn sử dụng trang nhân viên',
                tourTitle: 'Tour hướng dẫn',
                tourDescription:
                    'Nhấn vào nút "Hướng dẫn sử dụng" ở góc phải màn hình để xem tour chi tiết các tính năng của trang quản lý nhân viên.',
                tourSteps: [
                    {
                        selector: '[data-tour="employee-list"]',
                        title: 'Trang quản lý nhân sự',
                        content:
                            'Trang quản lý toàn bộ nhân sự trong hệ thống với các tính năng: thêm/sửa/xoá nhân viên, quản lý phòng ban.',
                    },
                    {
                        selector: '[data-tour="create-employee-btn"]',
                        title: 'Thêm nhân viên',
                        content:
                            'Nhấn để tạo tài khoản cho nhân viên mới. Hệ thống tự động gửi email xác nhận.',
                    },
                    {
                        selector: '[data-tour="employee-filter-bar"]',
                        title: 'Bộ lọc',
                        content:
                            'Lọc nhân viên theo loại, giới tính, trạng thái, ngày sinh, phòng ban và vị trí.',
                    },
                    {
                        selector: '[data-tour="employee-search"]',
                        title: 'Tìm kiếm',
                        content:
                            'Tìm kiếm nhanh theo tên, mã NV, email hoặc số điện thoại.',
                    },
                    {
                        selector: '[data-tour="employee-data-table"]',
                        title: 'Bảng danh sách',
                        content:
                            'Hiển thị danh sách nhân viên với đầy đủ thông tin. Click vào ba chấm để thực hiện các hành động.',
                    },
                ],
            },
        ],
        []
    )

    useQuickHelp(options)

    return { options }
}

export default useEmployeeListQuickHelp
